"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ManifestModule = exports.FILE_TYPE = exports.CONTENT_TYPE = void 0;
const stack_1 = require("../types/stack");
const stack_2 = require("./stack");
const folder_1 = require("./folder");
const crypto_1 = require("@akord/crypto");
const bad_request_1 = require("../errors/bad-request");
exports.CONTENT_TYPE = "application/x.arweave-manifest+json";
exports.FILE_TYPE = "application/json";
const FILE_NAME = "manifest.json";
const MANIFEST_TYPE = "arweave/paths";
const MANIFEST_VERSION = "0.1.0";
const MANIFEST_DEFAULT_INDEX_FILE = "index.html";
const folder_2 = require("../types/folder");
class ManifestModule {
    constructor(wallet, api, service) {
        this.api = api;
        this.stackModule = new stack_2.StackModule(wallet, api, service, exports.CONTENT_TYPE);
        this.folderModule = new folder_1.FolderModule(wallet, api);
    }
    /**
     * @returns Promise with vault manifest node
     */
    get(vaultId, parentId) {
        return __awaiter(this, void 0, void 0, function* () {
            const byParentId = parentId ? parentId : folder_2.ROOT_FOLDER;
            const stacks = yield this.stackModule.listAll(vaultId, { parentId: byParentId });
            const manifest = stacks.find((stack) => stack.name === FILE_NAME);
            return manifest;
        });
    }
    /**
     * Get manifest version by index, return the latest version by default
     * @param  {string} vaultId
     * @param  {number} [index] manifest version index
     * @returns Promise with vault manifest JSON data
     */
    getVersion(vaultId, parentId, index) {
        return __awaiter(this, void 0, void 0, function* () {
            const manifest = yield this.get(vaultId, parentId);
            if (!manifest) {
                throw new bad_request_1.BadRequest("A vault manifest does not exist yet. Use akord.manifest.generate() to create it.");
            }
            const manifestFile = yield this.stackModule.getVersion(manifest.id, index, { responseType: 'arraybuffer' });
            return JSON.parse((0, crypto_1.arrayToString)(manifestFile.data));
        });
    }
    /**
     * @param  {string} vaultId
     * @param  {ManifestOptions} options index name, parent id, etc.
     * @returns Promise with corresponding transaction id
     */
    generate(vaultId, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const vault = yield this.api.getVault(vaultId);
            if (!vault.public) {
                throw new bad_request_1.BadRequest("Manifest applies only to public vaults.");
            }
            let manifest = options === null || options === void 0 ? void 0 : options.manifest;
            if (!manifest) {
                manifest = yield this.renderManifestJSON(vaultId, options.parentId, options.indexName);
            }
            const manifestNode = yield this.get(vaultId, options.parentId);
            if (manifestNode) {
                // update vault manifest
                return yield this.stackModule.uploadRevision(manifestNode.id, [JSON.stringify(manifest)], { name: FILE_NAME, mimeType: exports.FILE_TYPE });
            }
            else {
                // create new vault manifest
                return yield this.stackModule.create(vaultId, [JSON.stringify(manifest)], { name: FILE_NAME, mimeType: exports.FILE_TYPE, parentId: options.parentId });
            }
        });
    }
    /**
     *
     * @returns manifest in json format
     */
    renderManifestJSON(vaultId, parentId, indexName) {
        return __awaiter(this, void 0, void 0, function* () {
            // takes a flat list of folders and stacks and generates a tree
            const treeify = (folders, stacks, parentId) => {
                // initalize our treelist with a root folder + stacks
                const treeList = [{ id: null, parentId: parentId, name: null, stacks: [] }];
                stacks.forEach((s) => {
                    if ((!parentId && !s["parentId"]) || s["parentId"] === parentId) {
                        treeList[0]["stacks"].push(s);
                    }
                });
                // setup a lookup table
                const lookup = {};
                folders.forEach(function (obj) {
                    obj["children"] = [];
                    obj["stacks"] = [];
                    if (obj["id"] !== parentId) {
                        lookup[obj["id"]] = obj;
                        // add the related stacks to this folder
                        stacks.forEach((s) => {
                            if (s["parentId"] === obj["id"])
                                obj["stacks"].push(s);
                        });
                    }
                });
                // add the folders to its parent folder (tree)
                folders.forEach((obj) => {
                    if (obj["parentId"] && obj["parentId"] !== parentId) {
                        lookup[obj["parentId"]]["children"].push(obj);
                    }
                    else if (!parentId || obj["parentId"] === parentId) {
                        treeList.push(obj);
                    }
                });
                return treeList;
            };
            // take the hierachical tree and compute the folder paths
            const computePaths = (tree, path) => {
                const paths = [];
                tree.forEach((folder) => {
                    folder['stacks'].forEach((stack) => {
                        // construct the path name
                        const pathName = [path, this.encode(folder['name']), this.encode(stack.name)]
                            .filter((p) => p != null)
                            .join("/");
                        const arweaveId = stack.getUri();
                        paths.push({
                            id: arweaveId,
                            path: pathName,
                        });
                    });
                    // process the children
                    if (folder['children']) {
                        let pathName = this.encode(folder['name']);
                        if (path)
                            pathName = [path, this.encode(folder['name'])].join("/");
                        const children = computePaths(folder['children'], pathName);
                        paths.push(...children);
                    }
                });
                return paths;
            };
            // load and clean list of folders
            const folders = (yield this.folderModule.listAll(vaultId, { limit: 1000 })).map((n) => {
                const { id, parentId, name } = n;
                return { id, parentId, name };
            });
            // load and clean list of stacks
            const stacks = (yield this.stackModule.listAll(vaultId, { limit: 1000 })).map((s) => {
                const { id, parentId, name, versions } = s;
                return new stack_1.Stack({ id, parentId, name, versions }, null);
            });
            const tree = treeify(folders, stacks, parentId);
            const paths = computePaths(tree, null);
            // map paths to manifest hash
            const manifest = {};
            paths.forEach((path) => {
                manifest[path.path] = { id: path.id };
            });
            return {
                manifest: MANIFEST_TYPE,
                version: MANIFEST_VERSION,
                index: {
                    path: indexName || MANIFEST_DEFAULT_INDEX_FILE,
                },
                paths: manifest,
            };
        });
    }
    ;
    encode(str) {
        if (str) {
            return encodeURIComponent(str);
        }
        return null;
    }
    ;
}
exports.ManifestModule = ManifestModule;
;
